import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import {
  useGetAllBusinessSetupDataQuery,
  useCreateMarketAnalysisMutation,
  useUpdateMarketAnalysisMutation,
  useCreateBusinessModelMutation,
  useUpdateBusinessModelMutation,
  useCreateRevenueForecastMutation,
  useUpdateRevenueForecastMutation,
  useCreateFundingPlanMutation,
  useUpdateFundingPlanMutation,
  useCreateExitStrategyMutation,
  useUpdateExitStrategyMutation,
  useCreateFullPlanMutation,
  useUpdateFullPlanMutation,
  useCreateReviewPlanMutation,
  useUpdateReviewPlanMutation,
} from '../../../../../../../Store/apis/clientApi';

// Initial state for all business setup data
const initialState = {
  // Market Analysis
  marketAnalysis: {
    data: null,
    loading: false,
    error: null,
    completed: false,
    loadedFromBackend: false, // Track if data was loaded from backend
  },
  // Business Model
  businessModel: {
    data: null,
    loading: false,
    error: null,
    completed: false,
    loadedFromBackend: false, // Track if data was loaded from backend
  },
  // Revenue Forecast
  revenueForecast: {
    data: null,
    loading: false,
    error: null,
    completed: false,
    loadedFromBackend: false, // Track if data was loaded from backend
  },
  // Funding Plan
  fundingPlan: {
    data: null,
    loading: false,
    error: null,
    completed: false,
    loadedFromBackend: false, // Track if data was loaded from backend
  },
  // Exit Strategy
  exitStrategy: {
    data: null,
    loading: false,
    error: null,
    completed: false,
    loadedFromBackend: false, // Track if data was loaded from backend
  },
  // Full Plan
  fullPlan: {
    data: null,
    loading: false,
    error: null,
    completed: false,
    loadedFromBackend: false, // Track if data was loaded from backend
  },
  // Review Plan
  reviewPlan: {
    data: null,
    loading: false,
    error: null,
    completed: false,
    loadedFromBackend: false, // Track if data was loaded from backend
  },
  // Global loading state
  isLoading: false,
  // Current step
  currentStep: 1,
  // User context
  userIdea: '',
  customerSegment: [],
  targetMarket: [],
  geography: [],
};

// Action types
const ACTION_TYPES = {
  // Generic actions
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',

  // Market Analysis actions
  SET_MARKET_ANALYSIS_DATA: 'SET_MARKET_ANALYSIS_DATA',
  SET_MARKET_ANALYSIS_LOADING: 'SET_MARKET_ANALYSIS_LOADING',
  SET_MARKET_ANALYSIS_ERROR: 'SET_MARKET_ANALYSIS_ERROR',
  SET_MARKET_ANALYSIS_COMPLETED: 'SET_MARKET_ANALYSIS_COMPLETED',

  // Business Model actions
  SET_BUSINESS_MODEL_DATA: 'SET_BUSINESS_MODEL_DATA',
  SET_BUSINESS_MODEL_LOADING: 'SET_BUSINESS_MODEL_LOADING',
  SET_BUSINESS_MODEL_ERROR: 'SET_BUSINESS_MODEL_ERROR',
  SET_BUSINESS_MODEL_COMPLETED: 'SET_BUSINESS_MODEL_COMPLETED',

  // Revenue Forecast actions
  SET_REVENUE_FORECAST_DATA: 'SET_REVENUE_FORECAST_DATA',
  SET_REVENUE_FORECAST_LOADING: 'SET_REVENUE_FORECAST_LOADING',
  SET_REVENUE_FORECAST_ERROR: 'SET_REVENUE_FORECAST_ERROR',
  SET_REVENUE_FORECAST_COMPLETED: 'SET_REVENUE_FORECAST_COMPLETED',

  // Funding Plan actions
  SET_FUNDING_PLAN_DATA: 'SET_FUNDING_PLAN_DATA',
  SET_FUNDING_PLAN_LOADING: 'SET_FUNDING_PLAN_LOADING',
  SET_FUNDING_PLAN_ERROR: 'SET_FUNDING_PLAN_ERROR',
  SET_FUNDING_PLAN_COMPLETED: 'SET_FUNDING_PLAN_COMPLETED',

  // Exit Strategy actions
  SET_EXIT_STRATEGY_DATA: 'SET_EXIT_STRATEGY_DATA',
  SET_EXIT_STRATEGY_LOADING: 'SET_EXIT_STRATEGY_LOADING',
  SET_EXIT_STRATEGY_ERROR: 'SET_EXIT_STRATEGY_ERROR',
  SET_EXIT_STRATEGY_COMPLETED: 'SET_EXIT_STRATEGY_COMPLETED',

  // Full Plan actions
  SET_FULL_PLAN_DATA: 'SET_FULL_PLAN_DATA',
  SET_FULL_PLAN_LOADING: 'SET_FULL_PLAN_LOADING',
  SET_FULL_PLAN_ERROR: 'SET_FULL_PLAN_ERROR',
  SET_FULL_PLAN_COMPLETED: 'SET_FULL_PLAN_COMPLETED',

  // Review Plan actions
  SET_REVIEW_PLAN_DATA: 'SET_REVIEW_PLAN_DATA',
  SET_REVIEW_PLAN_LOADING: 'SET_REVIEW_PLAN_LOADING',
  SET_REVIEW_PLAN_ERROR: 'SET_REVIEW_PLAN_ERROR',
  SET_REVIEW_PLAN_COMPLETED: 'SET_REVIEW_PLAN_COMPLETED',

  // Global actions
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  SET_USER_CONTEXT: 'SET_USER_CONTEXT',
  LOAD_ALL_DATA: 'LOAD_ALL_DATA',
};

// Reducer function
const businessSetupReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload };

    case ACTION_TYPES.CLEAR_ERROR:
      return { ...state, error: null };

    // Market Analysis
    case ACTION_TYPES.SET_MARKET_ANALYSIS_DATA:
      return {
        ...state,
        marketAnalysis: { ...state.marketAnalysis, data: action.payload, error: null, loadedFromBackend: !!action.payload }
      };
    case ACTION_TYPES.SET_MARKET_ANALYSIS_LOADING:
      return {
        ...state,
        marketAnalysis: { ...state.marketAnalysis, loading: action.payload }
      };
    case ACTION_TYPES.SET_MARKET_ANALYSIS_ERROR:
      return {
        ...state,
        marketAnalysis: { ...state.marketAnalysis, error: action.payload, loading: false }
      };
    case ACTION_TYPES.SET_MARKET_ANALYSIS_COMPLETED:
      return {
        ...state,
        marketAnalysis: { ...state.marketAnalysis, completed: action.payload }
      };

    // Business Model
    case ACTION_TYPES.SET_BUSINESS_MODEL_DATA:
      return {
        ...state,
        businessModel: { ...state.businessModel, data: action.payload, error: null, loadedFromBackend: !!action.payload }
      };
    case ACTION_TYPES.SET_BUSINESS_MODEL_LOADING:
      return {
        ...state,
        businessModel: { ...state.businessModel, loading: action.payload }
      };
    case ACTION_TYPES.SET_BUSINESS_MODEL_ERROR:
      return {
        ...state,
        businessModel: { ...state.businessModel, error: action.payload, loading: false }
      };
    case ACTION_TYPES.SET_BUSINESS_MODEL_COMPLETED:
      return {
        ...state,
        businessModel: { ...state.businessModel, completed: action.payload }
      };

    // Revenue Forecast
    case ACTION_TYPES.SET_REVENUE_FORECAST_DATA:
      return {
        ...state,
        revenueForecast: { ...state.revenueForecast, data: action.payload, error: null, loadedFromBackend: !!action.payload }
      };
    case ACTION_TYPES.SET_REVENUE_FORECAST_LOADING:
      return {
        ...state,
        revenueForecast: { ...state.revenueForecast, loading: action.payload }
      };
    case ACTION_TYPES.SET_REVENUE_FORECAST_ERROR:
      return {
        ...state,
        revenueForecast: { ...state.revenueForecast, error: action.payload, loading: false }
      };
    case ACTION_TYPES.SET_REVENUE_FORECAST_COMPLETED:
      return {
        ...state,
        revenueForecast: { ...state.revenueForecast, completed: action.payload }
      };

    // Funding Plan
    case ACTION_TYPES.SET_FUNDING_PLAN_DATA:
      return {
        ...state,
        fundingPlan: { ...state.fundingPlan, data: action.payload, error: null, loadedFromBackend: !!action.payload }
      };
    case ACTION_TYPES.SET_FUNDING_PLAN_LOADING:
      return {
        ...state,
        fundingPlan: { ...state.fundingPlan, loading: action.payload }
      };
    case ACTION_TYPES.SET_FUNDING_PLAN_ERROR:
      return {
        ...state,
        fundingPlan: { ...state.fundingPlan, error: action.payload, loading: false }
      };
    case ACTION_TYPES.SET_FUNDING_PLAN_COMPLETED:
      return {
        ...state,
        fundingPlan: { ...state.fundingPlan, completed: action.payload }
      };

    // Exit Strategy
    case ACTION_TYPES.SET_EXIT_STRATEGY_DATA:
      return {
        ...state,
        exitStrategy: { ...state.exitStrategy, data: action.payload, error: null, loadedFromBackend: !!action.payload }
      };
    case ACTION_TYPES.SET_EXIT_STRATEGY_LOADING:
      return {
        ...state,
        exitStrategy: { ...state.exitStrategy, loading: action.payload }
      };
    case ACTION_TYPES.SET_EXIT_STRATEGY_ERROR:
      return {
        ...state,
        exitStrategy: { ...state.exitStrategy, error: action.payload, loading: false }
      };
    case ACTION_TYPES.SET_EXIT_STRATEGY_COMPLETED:
      return {
        ...state,
        exitStrategy: { ...state.exitStrategy, completed: action.payload }
      };

    // Full Plan
    case ACTION_TYPES.SET_FULL_PLAN_DATA:
      return {
        ...state,
        fullPlan: { ...state.fullPlan, data: action.payload, error: null, loadedFromBackend: !!action.payload }
      };
    case ACTION_TYPES.SET_FULL_PLAN_LOADING:
      return {
        ...state,
        fullPlan: { ...state.fullPlan, loading: action.payload }
      };
    case ACTION_TYPES.SET_FULL_PLAN_ERROR:
      return {
        ...state,
        fullPlan: { ...state.fullPlan, error: action.payload, loading: false }
      };
    case ACTION_TYPES.SET_FULL_PLAN_COMPLETED:
      return {
        ...state,
        fullPlan: { ...state.fullPlan, completed: action.payload }
      };

    // Review Plan
    case ACTION_TYPES.SET_REVIEW_PLAN_DATA:
      return {
        ...state,
        reviewPlan: { ...state.reviewPlan, data: action.payload, error: null, loadedFromBackend: !!action.payload }
      };
    case ACTION_TYPES.SET_REVIEW_PLAN_LOADING:
      return {
        ...state,
        reviewPlan: { ...state.reviewPlan, loading: action.payload }
      };
    case ACTION_TYPES.SET_REVIEW_PLAN_ERROR:
      return {
        ...state,
        reviewPlan: { ...state.reviewPlan, error: action.payload, loading: false }
      };
    case ACTION_TYPES.SET_REVIEW_PLAN_COMPLETED:
      return {
        ...state,
        reviewPlan: { ...state.reviewPlan, completed: action.payload }
      };

    // Global actions
    case ACTION_TYPES.SET_CURRENT_STEP:
      return { ...state, currentStep: action.payload };

    case ACTION_TYPES.SET_USER_CONTEXT:
      return {
        ...state,
        userIdea: action.payload.userIdea || state.userIdea,
        customerSegment: action.payload.customerSegment || state.customerSegment,
        targetMarket: action.payload.targetMarket || state.targetMarket,
        geography: action.payload.geography || state.geography,
      };

    case ACTION_TYPES.LOAD_ALL_DATA:
      return { ...state, ...action.payload };

    default:
      return state;
  }
};

// Create context
const BusinessSetupContext = createContext();

// Provider component
export const BusinessSetupProvider = ({ children }) => {
  const [state, dispatch] = useReducer(businessSetupReducer, initialState);
  const token = useSelector((state) => state.auth.Token);
  const reduxDispatch = useDispatch();

  // Unified API hook - loads ALL data at once
  const { data: allBusinessSetupData, isLoading: allDataLoading, error: allDataError, refetch: refetchAllData } = useGetAllBusinessSetupDataQuery();


  // Extract individual data from unified response
  const marketAnalysisData = allBusinessSetupData?.market_analysis;
  const businessModelData = allBusinessSetupData?.business_model;
  const revenueForecastData = allBusinessSetupData?.revenue_forecast;
  const fundingPlanData = allBusinessSetupData?.funding_plan;
  const exitStrategyData = allBusinessSetupData?.exit_strategy;
  const fullPlanData = allBusinessSetupData?.full_plan;
  const reviewPlanData = allBusinessSetupData?.review_plan;

  // Individual loading states from context (not unified)
  const marketAnalysisLoading = state.marketAnalysis.loading;
  const businessModelLoading = state.businessModel.loading;
  const revenueForecastLoading = state.revenueForecast.loading;
  const fundingPlanLoading = state.fundingPlan.loading;
  const exitStrategyLoading = state.exitStrategy.loading;
  const fullPlanLoading = state.fullPlan.loading;
  const reviewPlanLoading = state.reviewPlan.loading;

  // Individual error states from context
  const marketAnalysisError = state.marketAnalysis.error;
  const businessModelError = state.businessModel.error;
  const revenueForecastError = state.revenueForecast.error;
  const fundingPlanError = state.fundingPlan.error;
  const exitStrategyError = state.exitStrategy.error;
  const fullPlanError = state.fullPlan.error;
  const reviewPlanError = state.reviewPlan.error;

  // Remove duplicate reduxDispatch declaration - it's already declared above

  // Mutation hooks
  const [createMarketAnalysis] = useCreateMarketAnalysisMutation();
  const [updateMarketAnalysis] = useUpdateMarketAnalysisMutation();
  const [createBusinessModel] = useCreateBusinessModelMutation();
  const [updateBusinessModel] = useUpdateBusinessModelMutation();
  const [createRevenueForecast] = useCreateRevenueForecastMutation();
  const [updateRevenueForecast] = useUpdateRevenueForecastMutation();
  const [createFundingPlan] = useCreateFundingPlanMutation();
  const [updateFundingPlan] = useUpdateFundingPlanMutation();
  const [createExitStrategy] = useCreateExitStrategyMutation();
  const [updateExitStrategy] = useUpdateExitStrategyMutation();
  const [createFullPlan] = useCreateFullPlanMutation();
  const [updateFullPlan] = useUpdateFullPlanMutation();
  const [createReviewPlan] = useCreateReviewPlanMutation();
  const [updateReviewPlan] = useUpdateReviewPlanMutation();

  // Helper functions for actions
  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
    if (error) {
      message.error(error);
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
  }, []);

  // Market Analysis actions
  const setMarketAnalysisData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_DATA, payload: data });
  }, []);

  const setMarketAnalysisLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_LOADING, payload: loading });
  }, []);

  const setMarketAnalysisError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_ERROR, payload: error });
  }, []);

  const setMarketAnalysisCompleted = useCallback((completed) => {
    dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_COMPLETED, payload: completed });
  }, []);

  // Business Model actions
  const setBusinessModelData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_DATA, payload: data });
  }, []);

  const setBusinessModelLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_LOADING, payload: loading });
  }, []);

  const setBusinessModelError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_ERROR, payload: error });
  }, []);

  const setBusinessModelCompleted = useCallback((completed) => {
    dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_COMPLETED, payload: completed });
  }, []);

  // Revenue Forecast actions
  const setRevenueForecastData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_DATA, payload: data });
  }, []);

  const setRevenueForecastLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_LOADING, payload: loading });
  }, []);

  const setRevenueForecastError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_ERROR, payload: error });
  }, []);

  const setRevenueForecastCompleted = useCallback((completed) => {
    dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_COMPLETED, payload: completed });
  }, []);

  // Funding Plan actions
  const setFundingPlanData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_DATA, payload: data });
  }, []);

  const setFundingPlanLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_LOADING, payload: loading });
  }, []);

  const setFundingPlanError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_ERROR, payload: error });
  }, []);

  const setFundingPlanCompleted = useCallback((completed) => {
    dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_COMPLETED, payload: completed });
  }, []);

  // Exit Strategy actions
  const setExitStrategyData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_DATA, payload: data });
  }, []);

  const setExitStrategyLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_LOADING, payload: loading });
  }, []);

  const setExitStrategyError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_ERROR, payload: error });
  }, []);

  const setExitStrategyCompleted = useCallback((completed) => {
    dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_COMPLETED, payload: completed });
  }, []);

  // Full Plan actions
  const setFullPlanData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_DATA, payload: data });
  }, []);

  const setFullPlanLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_LOADING, payload: loading });
  }, []);

  const setFullPlanError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_ERROR, payload: error });
  }, []);

  const setFullPlanCompleted = useCallback((completed) => {
    dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_COMPLETED, payload: completed });
  }, []);

  // Review Plan actions
  const setReviewPlanData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_DATA, payload: data });
  }, []);

  const setReviewPlanLoading = useCallback((loading) => {
    dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_LOADING, payload: loading });
  }, []);

  const setReviewPlanError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_ERROR, payload: error });
  }, []);

  const setReviewPlanCompleted = useCallback((completed) => {
    dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_COMPLETED, payload: completed });
  }, []);

  // Global actions
  const setCurrentStep = useCallback((step) => {
    dispatch({ type: ACTION_TYPES.SET_CURRENT_STEP, payload: step });
  }, []);

  const setUserContext = useCallback((context) => {
    dispatch({ type: ACTION_TYPES.SET_USER_CONTEXT, payload: context });
  }, []);

  // API call functions (will be implemented)
  const loadAllData = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      // Load all business setup data from backend
      // This will be implemented when we add the API endpoints
      console.log('Loading all business setup data...');
    } catch (error) {
      setError('Failed to load business setup data');
    } finally {
      setLoading(false);
    }
  }, [token, setLoading, setError]);

  // Load ALL data from unified API response
  useEffect(() => {
    console.log('BusinessSetupContext: Loading data from unified API', {
      hasData: !!allBusinessSetupData,
      isLoading: allDataLoading,
      hasError: !!allDataError
    });

    // Only process data when we have it and it's not loading
    if (allBusinessSetupData && !allDataLoading) {
      console.log('BusinessSetupContext: Processing unified data', allBusinessSetupData);

      // Safely load each step with proper null checks
      try {
        // Load Market Analysis
        if (allBusinessSetupData.market_analysis && typeof allBusinessSetupData.market_analysis === 'object') {
          console.log('BusinessSetupContext: Loading market analysis data');
          dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_DATA, payload: allBusinessSetupData.market_analysis });
          dispatch({
            type: ACTION_TYPES.SET_MARKET_ANALYSIS_COMPLETED,
            payload: Boolean(allBusinessSetupData.market_analysis.step_completed)
          });

          // Also set user context data from market analysis
          dispatch({
            type: ACTION_TYPES.SET_USER_CONTEXT,
            payload: {
              userIdea: allBusinessSetupData.market_analysis.idea || '',
              customerSegment: Array.isArray(allBusinessSetupData.market_analysis.customer_segment)
                ? allBusinessSetupData.market_analysis.customer_segment : [],
              targetMarket: Array.isArray(allBusinessSetupData.market_analysis.target_market)
                ? allBusinessSetupData.market_analysis.target_market : [],
              geography: Array.isArray(allBusinessSetupData.market_analysis.geography)
                ? allBusinessSetupData.market_analysis.geography : [],
            }
          });
        }

        // Load Business Model
        if (allBusinessSetupData.business_model && typeof allBusinessSetupData.business_model === 'object') {
          console.log('BusinessSetupContext: Loading business model data');
          dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_DATA, payload: allBusinessSetupData.business_model });
          dispatch({
            type: ACTION_TYPES.SET_BUSINESS_MODEL_COMPLETED,
            payload: Boolean(allBusinessSetupData.business_model.step_completed)
          });
        }

        // Load Revenue Forecast
        if (allBusinessSetupData.revenue_forecast && typeof allBusinessSetupData.revenue_forecast === 'object') {
          console.log('BusinessSetupContext: Loading revenue forecast data');
          dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_DATA, payload: allBusinessSetupData.revenue_forecast });
          dispatch({
            type: ACTION_TYPES.SET_REVENUE_FORECAST_COMPLETED,
            payload: Boolean(allBusinessSetupData.revenue_forecast.step_completed)
          });
        }

        // Load Funding Plan
        if (allBusinessSetupData.funding_plan && typeof allBusinessSetupData.funding_plan === 'object') {
          console.log('BusinessSetupContext: Loading funding plan data');
          dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_DATA, payload: allBusinessSetupData.funding_plan });
          dispatch({
            type: ACTION_TYPES.SET_FUNDING_PLAN_COMPLETED,
            payload: Boolean(allBusinessSetupData.funding_plan.step_completed)
          });
        }

        // Load Exit Strategy
        if (allBusinessSetupData.exit_strategy && typeof allBusinessSetupData.exit_strategy === 'object') {
          console.log('BusinessSetupContext: Loading exit strategy data');
          dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_DATA, payload: allBusinessSetupData.exit_strategy });
          dispatch({
            type: ACTION_TYPES.SET_EXIT_STRATEGY_COMPLETED,
            payload: Boolean(allBusinessSetupData.exit_strategy.step_completed)
          });
        }

        // Load Full Plan
        if (allBusinessSetupData.full_plan && typeof allBusinessSetupData.full_plan === 'object') {
          console.log('BusinessSetupContext: Loading full plan data');
          dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_DATA, payload: allBusinessSetupData.full_plan });
          dispatch({
            type: ACTION_TYPES.SET_FULL_PLAN_COMPLETED,
            payload: Boolean(allBusinessSetupData.full_plan.step_completed)
          });
        }

        // Load Review Plan
        if (allBusinessSetupData.review_plan && typeof allBusinessSetupData.review_plan === 'object') {
          console.log('BusinessSetupContext: Loading review plan data');
          dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_DATA, payload: allBusinessSetupData.review_plan });
          dispatch({
            type: ACTION_TYPES.SET_REVIEW_PLAN_COMPLETED,
            payload: Boolean(allBusinessSetupData.review_plan.step_completed)
          });
        }

        console.log('BusinessSetupContext: All data loaded successfully');
      } catch (error) {
        console.error('BusinessSetupContext: Error processing unified data:', error);
        dispatch({ type: ACTION_TYPES.SET_ERROR, payload: 'Failed to process business setup data' });
      }
    }

    // Only set loading states to true during initial unified API loading
    // Individual mutations will manage their own loading states
    if (allDataLoading && !allBusinessSetupData) {
      console.log('BusinessSetupContext: Initial unified API loading - setting all loading states to true');
      dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_LOADING, payload: true });
      dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_LOADING, payload: true });
      dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_LOADING, payload: true });
      dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_LOADING, payload: true });
      dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_LOADING, payload: true });
      dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_LOADING, payload: true });
      dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_LOADING, payload: true });
    } else if (!allDataLoading && allBusinessSetupData) {
      // Set loading states to false when unified API finishes loading
      // This is safe because mutations will override this if they're still running
      console.log('BusinessSetupContext: Unified API finished loading - resetting all loading states to false');
      dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_LOADING, payload: false });
      dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_LOADING, payload: false });
      dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_LOADING, payload: false });
      dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_LOADING, payload: false });
      dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_LOADING, payload: false });
      dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_LOADING, payload: false });
      dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_LOADING, payload: false });
    }

    // Set error states for all steps if there's an error
    if (allDataError) {
      console.error('BusinessSetupContext: Unified API error:', allDataError);
      const errorMessage = allDataError.message || 'Failed to load business setup data';
      dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_ERROR, payload: errorMessage });
      dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_ERROR, payload: errorMessage });
      dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_ERROR, payload: errorMessage });
      dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_ERROR, payload: errorMessage });
      dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_ERROR, payload: errorMessage });
      dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_ERROR, payload: errorMessage });
      dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_ERROR, payload: errorMessage });
    }
  }, [allBusinessSetupData, allDataLoading, allDataError]);


  // Cache invalidation using RTK Query tags with optimization
  const invalidateCaches = useCallback(async () => {
    try {
      // Use RTK Query's invalidation mechanism for better performance
      // This will invalidate all queries with the specified tags
      reduxDispatch({
        type: 'api/invalidateTags',
        payload: [
          'MarketAnalysis',
          'BusinessModel',
          'RevenueForecast',
          'FundingPlan',
          'ExitStrategy',
          'FullPlan',
          'ReviewPlan',
          'BusinessSetupProgress'
        ]
      });

      // Add a small delay to allow cache invalidation to complete
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log('BusinessSetupContext: Cache tags invalidated');
    } catch (error) {
      console.error('BusinessSetupContext: Error invalidating caches:', error);
    }
  }, [reduxDispatch]);

  // Optimistic update helpers
  const optimisticUpdate = useCallback((stepKey, data, isCompleted = false) => {
    const updateAction = ACTION_TYPES[`SET_${stepKey.toUpperCase()}_DATA`];
    const completedAction = ACTION_TYPES[`SET_${stepKey.toUpperCase()}_COMPLETED`];

    if (updateAction) {
      dispatch({ type: updateAction, payload: { ...data, step_completed: isCompleted } });
    }
    if (completedAction && isCompleted !== undefined) {
      dispatch({ type: completedAction, payload: isCompleted });
    }
  }, []);

  // API action functions
  const saveMarketAnalysis = useCallback(async (data) => {
    try {
      console.log('BusinessSetupContext: Starting saveMarketAnalysis');

      // Optimistic update for better UX
      const isCompleted = data.step_completed || (data.tam && data.sam && data.som);
      optimisticUpdate('market_analysis', data, isCompleted);

      dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_LOADING, payload: true });

      if (marketAnalysisData?.id) {
        console.log('BusinessSetupContext: Updating existing market analysis');
        await updateMarketAnalysis({ id: marketAnalysisData.id, ...data }).unwrap();
      } else {
        console.log('BusinessSetupContext: Creating new market analysis');
        await createMarketAnalysis(data).unwrap();
      }

      // Invalidate all caches for consistent state
      await invalidateCaches();

      console.log('BusinessSetupContext: Save completed successfully');
      message.success('Market analysis saved successfully!');
    } catch (error) {
      console.error('BusinessSetupContext: Save failed:', error);
      dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_ERROR, payload: error.message });

      // Revert optimistic update on failure
      if (marketAnalysisData) {
        dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_DATA, payload: marketAnalysisData });
      }

      throw error;
    } finally {
      dispatch({ type: ACTION_TYPES.SET_MARKET_ANALYSIS_LOADING, payload: false });
    }
  }, [marketAnalysisData, createMarketAnalysis, updateMarketAnalysis, optimisticUpdate, invalidateCaches]);

  const saveBusinessModel = useCallback(async (data) => {
    try {
      console.log('BusinessSetupContext: Starting saveBusinessModel');

      // Optimistic update for better UX
      const isCompleted = data.step_completed || (data.bm_analysis);
      optimisticUpdate('business_model', data, isCompleted);

      dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_LOADING, payload: true });

      if (businessModelData?.id) {
        console.log('BusinessSetupContext: Updating existing business model');
        await updateBusinessModel({ id: businessModelData.id, ...data }).unwrap();
      } else {
        console.log('BusinessSetupContext: Creating new business model');
        await createBusinessModel(data).unwrap();
      }

      // Invalidate all caches for consistent state
      await invalidateCaches();

      console.log('BusinessSetupContext: Save completed successfully');
      message.success('Business model saved successfully!');
    } catch (error) {
      console.error('BusinessSetupContext: Save failed:', error);
      dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_ERROR, payload: error.message });

      // Revert optimistic update on failure
      if (businessModelData) {
        dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_DATA, payload: businessModelData });
      }

      throw error;
    } finally {
      dispatch({ type: ACTION_TYPES.SET_BUSINESS_MODEL_LOADING, payload: false });
    }
  }, [businessModelData, createBusinessModel, updateBusinessModel, optimisticUpdate, invalidateCaches]);

  const saveRevenueForecast = useCallback(async (data) => {
    try {
      // Optimistic update for better UX
      const isCompleted = data.step_completed || (data.forecast_analysis);
      optimisticUpdate('revenue_forecast', data, isCompleted);

      dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_LOADING, payload: true });

      if (revenueForecastData?.id) {
        await updateRevenueForecast({ id: revenueForecastData.id, ...data }).unwrap();
      } else {
        await createRevenueForecast(data).unwrap();
      }

      // Invalidate all caches for consistent state
      await invalidateCaches();

      message.success('Revenue forecast saved successfully!');
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_ERROR, payload: error.message });

      // Revert optimistic update on failure
      if (revenueForecastData) {
        dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_DATA, payload: revenueForecastData });
      }

      throw error;
    } finally {
      dispatch({ type: ACTION_TYPES.SET_REVENUE_FORECAST_LOADING, payload: false });
    }
  }, [revenueForecastData, createRevenueForecast, updateRevenueForecast, optimisticUpdate, invalidateCaches]);

  const saveFundingPlan = useCallback(async (data) => {
    try {
      dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_LOADING, payload: true });
      if (fundingPlanData?.id) {
        await updateFundingPlan({ id: fundingPlanData.id, ...data }).unwrap();
      } else {
        await createFundingPlan(data).unwrap();
      }
      message.success('Funding plan saved successfully!');
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: ACTION_TYPES.SET_FUNDING_PLAN_LOADING, payload: false });
    }
  }, [fundingPlanData, createFundingPlan, updateFundingPlan]);

  const saveExitStrategy = useCallback(async (data) => {
    try {
      dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_LOADING, payload: true });
      if (exitStrategyData?.id) {
        await updateExitStrategy({ id: exitStrategyData.id, ...data }).unwrap();
      } else {
        await createExitStrategy(data).unwrap();
      }
      message.success('Exit strategy saved successfully!');
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: ACTION_TYPES.SET_EXIT_STRATEGY_LOADING, payload: false });
    }
  }, [exitStrategyData, createExitStrategy, updateExitStrategy]);

  const saveFullPlan = useCallback(async (data) => {
    try {
      dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_LOADING, payload: true });
      if (fullPlanData?.id) {
        await updateFullPlan({ id: fullPlanData.id, ...data }).unwrap();
      } else {
        await createFullPlan(data).unwrap();
      }
      message.success('Full plan saved successfully!');
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: ACTION_TYPES.SET_FULL_PLAN_LOADING, payload: false });
    }
  }, [fullPlanData, createFullPlan, updateFullPlan]);

  const saveReviewPlan = useCallback(async (data) => {
    try {
      dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_LOADING, payload: true });
      if (reviewPlanData?.id) {
        await updateReviewPlan({ id: reviewPlanData.id, ...data }).unwrap();
      } else {
        await createReviewPlan(data).unwrap();
      }
      message.success('Review plan saved successfully!');
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: ACTION_TYPES.SET_REVIEW_PLAN_LOADING, payload: false });
    }
  }, [reviewPlanData, createReviewPlan, updateReviewPlan]);

  const contextValue = {
    state,
    actions: {
      setLoading,
      setError,
      clearError,
      setMarketAnalysisData,
      setMarketAnalysisLoading,
      setMarketAnalysisError,
      setMarketAnalysisCompleted,
      setBusinessModelData,
      setBusinessModelLoading,
      setBusinessModelError,
      setBusinessModelCompleted,
      setRevenueForecastData,
      setRevenueForecastLoading,
      setRevenueForecastError,
      setRevenueForecastCompleted,
      setFundingPlanData,
      setFundingPlanLoading,
      setFundingPlanError,
      setFundingPlanCompleted,
      setExitStrategyData,
      setExitStrategyLoading,
      setExitStrategyError,
      setExitStrategyCompleted,
      setFullPlanData,
      setFullPlanLoading,
      setFullPlanError,
      setFullPlanCompleted,
      setReviewPlanData,
      setReviewPlanLoading,
      setReviewPlanError,
      setReviewPlanCompleted,
      setCurrentStep,
      setUserContext,
      loadAllData,
      // API save functions
      saveMarketAnalysis,
      saveBusinessModel,
      saveRevenueForecast,
      saveFundingPlan,
      saveExitStrategy,
      saveFullPlan,
      saveReviewPlan,
      // Cache and optimistic update functions
      invalidateCaches,
      optimisticUpdate,
    },
  };

  return (
    <BusinessSetupContext.Provider value={contextValue}>
      {children}
    </BusinessSetupContext.Provider>
  );
};

// Custom hook to use the context
export const useBusinessSetup = () => {
  const context = useContext(BusinessSetupContext);
  if (!context) {
    throw new Error('useBusinessSetup must be used within a BusinessSetupProvider');
  }
  return context;
};

export default BusinessSetupContext;
